import "./CardReveal.css";
import { ClassNames } from "@emotion/react";
import { AuthClient, createAuthClient } from "@spp/auth";
import {
  addVaultMessageListener,
  postVaultMessage,
  VaultCardRevealMessage,
  VaultFieldConfig,
  VaultFieldErrorMessage,
} from "@spp/vault";
import { getEnv } from "lib/env";
import {
  administerDigitalCard,
  CARD_REVEAL_TIMEOUT,
  formatCardNumber,
  GetDigitalCardRedactedResponse,
  GetDigitalCardUnredactedResponse,
  getErrorMessage,
  getVaultFieldFrames,
  isCardRevealField,
} from "lib/vault";
import { Eye, EyeOff } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

export type CardRevealProps = {
  fieldConfig: VaultFieldConfig;
};

export function CardReveal({ fieldConfig }: CardRevealProps) {
  const [authClient, setAuthClient] = useState<AuthClient | null>(null);
  const [cardRevealRedacted, setCardRevealRedacted] = useState<
    GetDigitalCardRedactedResponse["GetDigitalCardRedactedResponse"] | null
  >(null);
  const [cardReveaUnredacted, setCardRevealUnredacted] = useState<
    GetDigitalCardUnredactedResponse["GetDigitalCardUnredactedResponse"] | null
  >(null);
  const [cardNumberReveal, setCardNumberReveal] = useState<string | null>(null);
  const [cardCvcReveal, setCardCvcReveal] = useState<string | null>(null);
  const [cardReveal, setCardReveal] = useState(false);
  const [cardRevealLoading, setCardRevealLoading] = useState(false);

  const cardRevealTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const formattedCardNumberReveal = useMemo(
    () => (cardNumberReveal ? formatCardNumber(cardNumberReveal) : null),
    [cardNumberReveal]
  );

  const cardRevealtext = useMemo(
    () =>
      fieldConfig?.fieldType === "cardNumberReveal"
        ? formattedCardNumberReveal
        : fieldConfig?.fieldType === "cardCvcReveal"
        ? cardCvcReveal
        : null,
    [cardCvcReveal, fieldConfig?.fieldType, formattedCardNumberReveal]
  );

  const getCardRevealRedacted = useCallback(async () => {
    if (cardRevealRedacted) return cardRevealRedacted;

    if (!authClient || !fieldConfig || !fieldConfig.digitalCreditCardId) return;

    const { response, error } = await administerDigitalCard(authClient, {
      GetDigitalCardRedactedRequest: {
        digitalCreditCardId: fieldConfig.digitalCreditCardId,
      },
    });

    if (error || !response) {
      postVaultMessage<VaultFieldErrorMessage>(window.parent, {
        type: "Error",
        errorMessage: getErrorMessage(error),
      });
      return;
    }

    if ("GetDigitalCardRedactedResponse" in response) {
      setCardRevealRedacted(response.GetDigitalCardRedactedResponse);
      return response.GetDigitalCardRedactedResponse;
    }
  }, [authClient, fieldConfig, cardRevealRedacted]);

  const getCardRevealUnredacted = useCallback(async () => {
    if (cardReveaUnredacted) return cardReveaUnredacted;

    if (!authClient || !fieldConfig || !fieldConfig.digitalCreditCardId) return;

    const { response, error } = await administerDigitalCard(authClient, {
      GetDigitalCardUnredactedRequest: {
        digitalCreditCardId: fieldConfig.digitalCreditCardId,
      },
    });

    if (error || !response) {
      postVaultMessage<VaultFieldErrorMessage>(window.parent, {
        type: "Error",
        errorMessage: getErrorMessage(error),
      });
      return;
    }

    if ("GetDigitalCardUnredactedResponse" in response) {
      setCardRevealUnredacted(response.GetDigitalCardUnredactedResponse);
      return response.GetDigitalCardUnredactedResponse;
    }
  }, [authClient, fieldConfig, cardReveaUnredacted]);

  const hideCardReveal = useCallback(async () => {
    setCardRevealLoading(true);

    // clear hide card reveal timeout
    if (cardRevealTimeoutRef.current) {
      clearTimeout(cardRevealTimeoutRef.current);
      cardRevealTimeoutRef.current = null;
    }

    const cardRevealRedacted = await getCardRevealRedacted();
    if (!cardRevealRedacted) return;

    const last4 = cardRevealRedacted.details.redactedPan.slice(-4);

    setCardNumberReveal(`************${last4}`);
    setCardCvcReveal("***");
    setCardReveal(false);
    setCardRevealLoading(false);
  }, [getCardRevealRedacted]);

  const showCardReveal = useCallback(async () => {
    if (!ref.current.fieldConfig) return;
    setCardRevealLoading(true);

    // clear hide card reveal timeout
    if (cardRevealTimeoutRef.current) {
      clearTimeout(cardRevealTimeoutRef.current);
      cardRevealTimeoutRef.current = null;
    }

    // hide other card reveal fields
    getVaultFieldFrames(window.parent, ref.current.fieldConfig.vaultId)
      .filter(
        (frame) =>
          frame !== window &&
          isCardRevealField(frame.fieldConfig?.fieldType ?? "")
      )
      .forEach((frame) => {
        postVaultMessage<VaultCardRevealMessage>(frame, {
          type: "CardReveal",
          cardReveal: false,
        });
      });

    const cardReveaUnredacted = await getCardRevealUnredacted();
    if (!cardReveaUnredacted) return;

    const { pan, cvv } = cardReveaUnredacted;

    // set hide card reveal timeout
    const timeout = setTimeout(() => {
      cardRevealTimeoutRef.current = null;
      hideCardReveal();
    }, CARD_REVEAL_TIMEOUT);
    cardRevealTimeoutRef.current = timeout;

    setCardNumberReveal(pan);
    setCardCvcReveal(cvv || "N/A");
    setCardReveal(true);
    setCardRevealLoading(false);
  }, [getCardRevealUnredacted, hideCardReveal]);

  const ref = useRef({
    fieldConfig,
    hideCardReveal,
    showCardReveal,
  });

  useEffect(() => {
    ref.current = {
      ...ref.current,
      fieldConfig,
      hideCardReveal,
      showCardReveal,
    };
  }, [fieldConfig, hideCardReveal, showCardReveal]);

  useEffect(() => {
    const removeMessageListener = addVaultMessageListener((message) => {
      if (message.type === "CardReveal") {
        message.cardReveal && ref.current.showCardReveal();
        !message.cardReveal && ref.current.hideCardReveal();
      }
    });

    return () => removeMessageListener();
  }, []);

  useEffect(() => {
    (async () => {
      const authClient = await createAuthClient({ env: await getEnv() });
      setAuthClient(authClient);
    })();
  }, []);

  useEffect(() => {
    if (!authClient || !fieldConfig) return;

    if (isCardRevealField(fieldConfig.fieldType)) {
      ref.current.hideCardReveal();
    }
  }, [authClient, fieldConfig]);

  const handleToggleCardReveal = useCallback(async () => {
    cardReveal && hideCardReveal();
    !cardReveal && showCardReveal();
  }, [cardReveal, hideCardReveal, showCardReveal]);

  if (!fieldConfig) return null;

  return (
    <ClassNames>
      {({ css, cx }) => (
        <div className={cx("card-reveal", css(fieldConfig.fieldStyles))}>
          {cardRevealtext && (
            <>
              <span className="card-reveal-text">{cardRevealtext}</span>
              <button
                type="button"
                className="card-reveal-button"
                onClick={handleToggleCardReveal}
                disabled={cardRevealLoading}
              >
                {cardReveal ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </>
          )}
        </div>
      )}
    </ClassNames>
  );
}
